import type { McpPrompt, McpServer } from "@codvero/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Globe, KeyRound, Plug, TerminalSquare, X, Zap } from "lucide-react";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiFailure } from "@/api/client.ts";
import { mcpApi } from "@/api/mcp.ts";
import { cn } from "@/lib/cn.ts";
import { Button, Input, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/**
 * "/" in a Chat-section message box: pick one of your MCP connections, then its shortcuts (the server's MCP prompts,
 * like "Get news" or "Make a carousel") sit in a bar above the box; a shortcut puts the server's prompt text in the box.
 * Returns the menu (placed over the box), the bar (above the textarea) and a key handler for the textarea.
 */
export function useMcpShortcuts(text: string, setText: (v: string | ((p: string) => string)) => void, focus: () => void): { menu: ReactNode; bar: ReactNode; onKeyDown: (e: KeyboardEvent) => boolean } {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const servers = useQuery({ queryKey: ["mcp-servers"], queryFn: mcpApi.list, staleTime: 60_000 });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ prompt: McpPrompt; values: Record<string, string> } | null>(null);
  const slash = /^\/(\S*)$/.exec(text);
  const options = slash ? (servers.data ?? []).filter((s) => s.enabled && s.name.includes(slash[1]!.toLowerCase())) : [];
  const active = (servers.data ?? []).find((s) => s.id === activeId) ?? null;
  const prompts = useQuery({ queryKey: ["mcp-prompts", activeId], queryFn: () => mcpApi.prompts(activeId!), enabled: !!active && active.transport === "http" && active.oauth !== "required", staleTime: 5 * 60_000, retry: false });
  const insert = useMutation({
    mutationFn: ({ prompt, values }: { prompt: McpPrompt; values: Record<string, string> }) => mcpApi.prompt(activeId!, prompt.name, values),
    onSuccess: (r) => { setAsking(null); setText((v) => (v.trim() ? `${v.replace(/\s*$/, "")}\n\n${r.text}` : r.text)); focus(); },
  });
  const choose = (s: McpServer) => { setActiveId(s.id); setAsking(null); setText(""); focus(); };
  // every required input filled in
  const ready = !!asking && asking.prompt.arguments.every((a) => !a.required || (asking.values[a.name] ?? "").trim());
  const use = (p: McpPrompt) => (p.arguments.length ? setAsking({ prompt: p, values: {} }) : insert.mutate({ prompt: p, values: {} }));

  const onKeyDown = (e: KeyboardEvent) => {
    if (!slash) return false;
    if (e.key === "Escape") { e.preventDefault(); setText(""); return true; }
    if (e.key === "Enter" && options[0]) { e.preventDefault(); choose(options[0]); return true; }
    return false;
  };

  const menu = slash ? (
    <ul className="absolute bottom-full start-2 z-30 mb-1 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-surface py-1 text-sm shadow-xl" role="listbox" aria-label={t("mcpShortcuts.servers")}>
      <li className="px-3 pb-1 pt-1.5 text-[11px] font-medium text-fg-muted">{t("mcpShortcuts.servers")}</li>
      {options.map((s) => (
        <li key={s.id}>
          <button type="button" role="option" aria-selected="false" onMouseDown={(e) => { e.preventDefault(); choose(s); }} className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-surface-2">
            {s.transport === "http" ? <Globe className="size-4 text-fg-muted" /> : <TerminalSquare className="size-4 text-fg-muted" />}
            <span dir="ltr" className="flex-1 truncate font-mono">/{s.name}</span>
            {s.oauth === "required" && <KeyRound className="size-3.5 text-warn" />}
          </button>
        </li>
      ))}
      {servers.data && options.length === 0 && (
        <li className="px-3 py-2 text-xs text-fg-muted">{servers.data.length ? t("mcpShortcuts.noMatch") : t("mcpShortcuts.none")} <Link to="/chat/mcp" className="text-accent underline">{t("chatTools.mcpTitle")}</Link></li>
      )}
    </ul>
  ) : null;

  const unauthorized = active?.oauth === "required" || (prompts.error instanceof ApiFailure && prompts.error.code === "unauthorized");
  const bar = active ? (
    <div className="space-y-2 border-b border-border px-3 py-2">
      <div className="flex items-center gap-2 overflow-x-auto">
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent-soft px-2 py-1 text-xs">
          <Plug className="size-3.5 text-accent" /><span dir="ltr" className="font-mono">{active.name}</span>
          <button type="button" aria-label={t("common.close")} onClick={() => { setActiveId(null); setAsking(null); }} className="text-fg-muted hover:text-fg"><X className="size-3.5" /></button>
        </span>
        {active.transport !== "http" ? (
          <span className="text-xs text-fg-muted">{t("mcpShortcuts.localNote")}</span>
        ) : unauthorized ? (
          <Link to="/chat/mcp" className="flex items-center gap-1 text-xs text-warn underline"><KeyRound className="size-3.5" /> {t("mcpShortcuts.connectFirst")}</Link>
        ) : prompts.isLoading ? (
          <Spinner className="size-4" />
        ) : prompts.error ? (
          <span className="truncate text-xs text-danger">{toMsg(prompts.error)}</span>
        ) : (prompts.data ?? []).length === 0 ? (
          <span className="text-xs text-fg-muted">{t("mcpShortcuts.empty")}</span>
        ) : (
          (prompts.data ?? []).map((p) => (
            <button key={p.name} type="button" disabled={insert.isPending} title={p.description ?? p.name} onClick={() => use(p)} className={cn("flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs hover:border-accent/60 disabled:opacity-50", asking?.prompt.name === p.name ? "border-accent bg-accent-soft" : "border-border")}>
              {insert.isPending && insert.variables?.prompt.name === p.name ? <Spinner className="size-3" /> : <Zap className="size-3 text-accent" />}
              <span dir="auto">{p.title ?? p.name}</span>
            </button>
          ))
        )}
      </div>
      {asking && (
        // not a <form>: it sits inside the message box's form, and a nested form submits natively (a page reload)
        <div className="flex flex-wrap items-end gap-2" onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); e.stopPropagation(); if (ready) insert.mutate(asking); } if (e.key === "Escape") { e.preventDefault(); setAsking(null); } }}>
          {asking.prompt.arguments.map((a, i) => (
            <label key={a.name} className="min-w-[10rem] flex-1 space-y-0.5 text-[11px] text-fg-muted">
              <span>{a.description ?? a.name}{a.required ? " *" : ""}</span>
              <Input autoFocus={i === 0} dir="auto" className="h-8 text-xs" value={asking.values[a.name] ?? ""} onChange={(e) => setAsking({ ...asking, values: { ...asking.values, [a.name]: e.target.value } })} />
            </label>
          ))}
          <Button type="button" size="sm" loading={insert.isPending} disabled={!ready} onClick={() => insert.mutate(asking)}>{t("mcpShortcuts.insert")}</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setAsking(null)}>{t("common.cancel")}</Button>
        </div>
      )}
      {insert.error && <p className="text-xs text-danger">{toMsg(insert.error)}</p>}
    </div>
  ) : null;

  return { menu, bar, onKeyDown };
}
